import React from 'react';
import { Text, RichText, Link, withSitecoreContext } from '@sitecore-jss/sitecore-jss-react';

const renderSubTitle = ({ field, classes, sitecoreContext }) => {
  if (!field) {
    return null;
  }

  if (!sitecoreContext.pageEditing && !field?.value) {
    return null;
  }

  return (
    <div
      aria-label={field?.value}
      id={field?.value?.replace(' ', '')}
      className={classes.join(' ')}
      tabIndex="0"
    >
      <Text field={field} />
    </div>
  );
};

const renderFeaturedHeading = ({ field, classes, sitecoreContext }) => {
  if (!field) {
    return null;
  }

  if (!sitecoreContext.pageEditing && !field?.value) {
    return null;
  }
  if (!sitecoreContext.pageEditing && field?.value) {
    return (
      <div
        className={`${classes}`}
        data-component="FeatureHeading"
        tabIndex="0"
        dangerouslySetInnerHTML={{ __html: field.value }}
      ></div>
    );
  }
  return (
    <div className={`${classes}`} data-component="FeatureHeading" tabIndex="0">
      <Text field={field} />
    </div>
  );
};

const renderPrimaryHeading = ({ fields, classes, headingType = null, sitecoreContext }) => {
  if (!sitecoreContext.pageEditing && !fields.PrimaryHeading?.value) {
    return null;
  }

  const renderHeadlingField = () => {
    if (sitecoreContext.pageEditing) {
      return <Text field={fields.PrimaryHeading} />;
    }

    return null;
  };
  const getRawHeadline = () => {
    if (!sitecoreContext.pageEditing) {
      return fields.PrimaryHeading.value?.replace('<p>', '').replace('</p>', '');
    }

    return null;
  };
  const rawHeadline = getRawHeadline();
  const type = headingType ?? fields.PrimaryHeadingType?.fields?.value?.value;
  if (!sitecoreContext.pageEditing) {
    switch (type) {
      case 'H1':
        return (
          <h1
            className={`${classes} u-text-lg-1 u-text-md-1 u-text-sm-1`}
            data-component="PrimaryHeading"
            tabIndex="0"
            dangerouslySetInnerHTML={{ __html: rawHeadline }}
          >
            {renderHeadlingField()}
          </h1>
        );
      case 'H2':
        return (
          <h2
            className={`${classes} u-text-lg-2 u-text-md-2 u-text-sm-2`}
            data-component="PrimaryHeading"
            tabIndex="0"
            dangerouslySetInnerHTML={{ __html: rawHeadline }}
          >
            {renderHeadlingField()}
          </h2>
        );
      default:
        return (
          <div
            className={`${classes} u-text-lg-2 u-text-md-2 u-text-sm-2`}
            data-component="PrimaryHeading"
            tabIndex="0"
            dangerouslySetInnerHTML={{ __html: rawHeadline }}
          >
            {renderHeadlingField()}
          </div>
        );
    }
  }
  if (sitecoreContext.pageEditing) {
    switch (type) {
      case 'H1':
        return (
          <h1
            className={`${classes} u-text-lg-1 u-text-md-1 u-text-sm-1`}
            data-component="PrimaryHeading"
            tabIndex="0"
          >
            {renderHeadlingField()}
          </h1>
        );
      case 'H2':
        return (
          <h2
            className={`${classes} u-text-lg-2 u-text-md-2 u-text-sm-2`}
            data-component="PrimaryHeading"
            tabIndex="0"
          >
            {renderHeadlingField()}
          </h2>
        );
      default:
        return (
          <div
            className={`${classes} u-text-lg-2 u-text-md-2 u-text-sm-2`}
            data-component="PrimaryHeading"
            tabIndex="0"
          >
            {renderHeadlingField()}
          </div>
        );
    }
  }
};

const renderSecondaryHeading = ({ field, classes, isHeading, sitecoreContext }) => {
  if (!field) {
    return null;
  }

  if (!sitecoreContext.pageEditing && !field?.value) {
    return null;
  }

  if (isHeading) {
    return (
      <h2
        className={`${classes} u-text-lg-3 u-text-md-3 u-text-sm-3`}
        data-component="SecondaryHeading"
        tabIndex="0"
      >
        <Text field={field}></Text>
      </h2>
    );
  }

  return (
    <div
      className={`${classes} u-text-lg-3 u-text-md-3 u-text-sm-3`}
      data-component="SecondaryHeading"
      tabIndex="0"
    >
      <Text field={field}></Text>
    </div>
  );
};

const renderText = ({ field, classes, isNarrow, sitecoreContext }) => {
  if (!field) {
    return null;
  }

  if (!sitecoreContext.pageEditing && !field?.value) {
    return null;
  }

  return (
    <RichText
      field={field}
      className={
        `${classes} u-text-body-lg u-text-body-md u-text-body-sm ` + (isNarrow ? 'is-narrow' : '')
      }
      data-component="TextDescription"
      tabIndex="0"
    />
  );
};

const renderCtaLink = ({ field, classes = '', isInverted, tracking, sitecoreContext }) => {
  if (!field) {
    return null;
  }

  if (!sitecoreContext.pageEditing && !field?.value?.href) {
    return null;
  }

  return (
    <Link
      field={field}
      className={`${classes} c-button c-cta-button ` + (isInverted ? 'is-secondary' : 'is-primary is-medium')}
      role="button"
      {...tracking}
    />
  );
};

const renderCtaLinkTheme = ({ field, classes = '', tracking, sitecoreContext }) => {
  if (!field) {
    return null;
  }

  if (!sitecoreContext.pageEditing && !field?.value?.href) {
    return null;
  }

  return (
    <Link
      field={field}
      className={`${classes} c-button c-cta-button`}
      role="button"
      {...tracking}
    />
  );
};

const subTitleWithContext = withSitecoreContext()(renderSubTitle);
const primaryHeadingWithContext = withSitecoreContext()(renderPrimaryHeading);
const featuredHeadingWithContext = withSitecoreContext()(renderFeaturedHeading);
const secondaryHeadingWithContext = withSitecoreContext()(renderSecondaryHeading);
const textWithContext = withSitecoreContext()(renderText);
const ctaLinkWithContext = withSitecoreContext()(renderCtaLink);
const ctaLinkThemeWithContext = withSitecoreContext()(renderCtaLinkTheme);

export {
  subTitleWithContext as renderSubTitle,
  featuredHeadingWithContext as renderFeaturedHeading,
  primaryHeadingWithContext as renderPrimaryHeading,
  secondaryHeadingWithContext as renderSecondaryHeading,
  textWithContext as renderText,
  ctaLinkWithContext as renderCtaLink,
  ctaLinkThemeWithContext as renderCtaLinkTheme,
};
